/**
 * PromptApplyDialog — Apply a prompt template into a target system-prompt
 * field with live `{{var}}` substitution (#151).
 *
 * Opened from the agent builder's "Apply prompt template" button. The
 * dialog:
 *
 *   1. Lists prompt templates the tenant can see (own + system + cloned).
 *   2. On selecting one, shows the variable form (rendered via
 *      VariableValueForm from studio/variable-editor).
 *   3. Renders a live preview of the substituted body that updates as
 *      the user types — semantics matches the server's `/apply` endpoint
 *      (literal split/join, no regex backreference expansion).
 *   4. Apply button is disabled until all required variables are filled
 *      (or have defaults). On click, calls `onApply(substitutedText)` and
 *      closes the dialog. The caller decides whether to overwrite or
 *      append to the agent's system_prompt field.
 *
 * NOT a clone-from-system flow — cloning is already wired in
 * PromptEditor.tsx's tenant scope. This dialog is read-only application
 * of an existing template (tenant-owned or system).
 *
 * USAGE:
 *
 *   <PromptApplyDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     onApply={(text) => updateConfig({ systemPrompt: text })}
 *   />
 *
 * The dialog reuses the existing `usePromptTemplates()` hook (same
 * source the Studio prompts catalog renders from), so anything the
 * user can see in `/studio/prompts` they can apply here.
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, Sparkles, ShieldCheck, Copy, Check, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  VariableValueForm,
  type PromptVariableEntry,
} from '@/components/studio/variable-editor';
import { usePromptTemplates, type PromptTemplate } from '@/lib/use-prompt-templates';
import {
  canApply,
  initialValues,
  missingRequiredVariables,
  substitutePromptBody,
} from './prompt-apply-helpers';

interface PromptApplyDialogProps {
  /** Controls dialog visibility (controlled). */
  open: boolean;
  /** Called when the user closes the dialog or after Apply succeeds. */
  onOpenChange: (open: boolean) => void;
  /**
   * Called when the user clicks Apply with the substituted body. The
   * caller decides what to do with it (typically: stuff it into the
   * agent builder's `systemPrompt` field).
   */
  onApply: (substitutedBody: string, source: PromptTemplate) => void;
  /**
   * Optional label override for the Apply button (e.g. "Use as system
   * prompt" in the agent builder context).
   */
  applyLabel?: string;
}

export function PromptApplyDialog({
  open,
  onOpenChange,
  onApply,
  applyLabel = 'Apply prompt',
}: PromptApplyDialogProps) {
  const { data, isLoading, error } = usePromptTemplates();
  const prompts = data?.data ?? [];

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  // Reset state when the dialog opens (avoid stale selection / values).
  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedId(null);
      setValues({});
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!search) return prompts;
    const q = search.toLowerCase();
    return prompts.filter(
      (p) =>
        p.command.toLowerCase().includes(q) ||
        p.title.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q),
    );
  }, [prompts, search]);

  const selected = useMemo(
    () => prompts.find((p) => p.id === selectedId) ?? null,
    [prompts, selectedId],
  );

  // When the user picks a template, seed the variable form with each
  // variable's default. The user can overwrite anything afterwards.
  useEffect(() => {
    if (selected) {
      setValues(initialValues(selected.variables as PromptVariableEntry[]));
    }
  }, [selected]);

  const variables: PromptVariableEntry[] = useMemo(
    () => (selected ? (selected.variables as PromptVariableEntry[]) : []),
    [selected],
  );

  const preview = useMemo(
    () => (selected ? substitutePromptBody(selected.content, variables, values) : ''),
    [selected, variables, values],
  );

  const missing = useMemo(
    () => (selected ? missingRequiredVariables(variables, values) : []),
    [selected, variables, values],
  );

  const enableApply = useMemo(
    () => (selected ? canApply(selected.content, variables, values) : false),
    [selected, variables, values],
  );

  const handleApply = () => {
    if (!selected || !enableApply) return;
    onApply(preview, selected);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" />
            Apply prompt template
          </DialogTitle>
          <DialogDescription>
            Pick a prompt template, fill in its variables, and apply the
            substituted body. System prompts authored by your platform
            administrator are marked. Tenant copies (cloned from system)
            stay editable in the Studio.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[260px_1fr] gap-3 flex-1 min-h-0">
          {/* ─── Left column: template picker ─────────────────────── */}
          <div className="flex flex-col border rounded min-h-0">
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-7 h-8 text-xs"
                  aria-label="Search prompt templates"
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              {isLoading ? (
                <div className="p-4 text-xs text-muted-foreground animate-pulse">
                  Loading prompts…
                </div>
              ) : error ? (
                <div className="p-4 text-xs text-destructive">
                  Failed to load prompts: {error.message}
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-4 text-xs text-muted-foreground italic">
                  No prompt templates found.
                </div>
              ) : (
                <ul className="p-1 space-y-0.5">
                  {filtered.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(p.id)}
                        className={cn(
                          'w-full text-left rounded px-2 py-1.5 text-xs hover:bg-muted/50 transition-colors',
                          selectedId === p.id && 'bg-muted',
                        )}
                        aria-pressed={selectedId === p.id}
                      >
                        <div className="flex items-center gap-1.5">
                          <code className="font-mono text-[11px] truncate">
                            /{p.command}
                          </code>
                          {p.isSystem && (
                            <Badge
                              variant="default"
                              className="text-[9px] gap-0.5 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20 px-1"
                            >
                              <ShieldCheck className="h-2 w-2" />
                              System
                            </Badge>
                          )}
                          {p.clonedFromSystemId && (
                            <Badge
                              variant="outline"
                              className="text-[9px] gap-0.5 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30 px-1"
                            >
                              <Copy className="h-2 w-2" />
                              Cloned
                            </Badge>
                          )}
                        </div>
                        <div className="text-[11px] font-medium mt-0.5 truncate">
                          {p.title}
                        </div>
                        {p.description && (
                          <div className="text-[10px] text-muted-foreground line-clamp-1">
                            {p.description}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </div>

          {/* ─── Right column: variable form + preview ─────────────── */}
          <div className="flex flex-col min-h-0">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground italic border rounded border-dashed">
                Select a prompt template on the left to begin.
              </div>
            ) : (
              <div className="flex flex-col min-h-0 gap-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      {selected.title}
                      <code className="text-xs font-mono text-muted-foreground">
                        /{selected.command}
                      </code>
                    </h3>
                    {selected.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {selected.description}
                      </p>
                    )}
                  </div>
                  {missing.length > 0 && (
                    <Badge
                      variant="outline"
                      className="text-[10px] gap-1 bg-amber-500/10 text-amber-700 border-amber-500/30 shrink-0"
                    >
                      {missing.length}{' '}
                      {missing.length === 1 ? 'variable' : 'variables'} required
                    </Badge>
                  )}
                </div>

                <div className="grid grid-rows-[auto_1fr] gap-3 flex-1 min-h-0">
                  {/* Variable input form */}
                  {variables.length > 0 ? (
                    <div className="space-y-1.5 rounded border p-3 bg-muted/20">
                      <Label className="text-xs font-semibold">Variables</Label>
                      <VariableValueForm
                        variables={variables}
                        values={values}
                        onChange={setValues}
                      />
                    </div>
                  ) : (
                    <div className="rounded border p-2 text-[11px] text-muted-foreground italic">
                      This template has no variables — apply it as-is.
                    </div>
                  )}

                  {/* Live preview */}
                  <div className="flex flex-col min-h-0">
                    <Label className="text-xs font-semibold mb-1">Preview</Label>
                    <ScrollArea className="flex-1 border rounded bg-background">
                      <pre className="p-3 text-xs whitespace-pre-wrap font-mono">
                        {preview || (
                          <span className="text-muted-foreground italic">
                            (empty)
                          </span>
                        )}
                      </pre>
                    </ScrollArea>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="pt-3 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            disabled={!enableApply}
            title={
              !selected
                ? 'Select a prompt template first.'
                : missing.length > 0
                  ? `Fill in: ${missing.join(', ')}`
                  : undefined
            }
          >
            <Check className="h-3.5 w-3.5 mr-1" />
            {applyLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
